import app from 'flarum/forum/app';
// Component lives at flarum/common/Component — NOT under components/. The
// wrong path resolves to undefined, and `class X extends undefined` throws at
// module load, killing the whole extension initializer silently.
import Component from 'flarum/common/Component';
import Link from 'flarum/common/components/Link';
import Button from 'flarum/common/components/Button';
import humanTime from 'flarum/common/helpers/humanTime';
import { coverFor } from './cover';
import { cdnImage } from './cdnImage';
import { cachedFeatured, cacheFeatured, setFeaturedRefresher } from '../featured';
import watchForNewUpdates from '../realtimeFeatured';
import m from 'mithril';

/**
 * The front page above the list: the latest Updates as featured cards.
 *
 * Fetches its own data rather than reading the index's list state, because the
 * hero renders before that state has resolved.
 */
export default class IndexHeader extends Component {
  loading = true;
  /** The newest BlizzNux Launcher release: the big card, and only ever that. */
  lead = null;
  /** Every other Update, newest first. `shown` is how many cards are on screen, lead included. */
  featured = [];
  /**
   * How many cards are on screen. A desktop row fits all six at once, so it
   * shows them and never offers the button; a phone stacks them, and six
   * full-width cards would bury the discussions, so it starts at three.
   */
  shown = (() => IndexHeader.initialShown())();

  /**
   * 820px is the same breakpoint the LESS uses to stack the cards — read from
   * matchMedia rather than duplicated as a number in two places that can
   * drift apart.
   */
  static PHONE = '(max-width: 820px)';
  static isPhone() {
    return typeof window !== 'undefined' && !!window.matchMedia?.(IndexHeader.PHONE).matches;
  }
  static initialShown() {
    return IndexHeader.isPhone() ? 3 : 6;
  }
  oninit(vnode) {
    super.oninit(vnode);
    this.load();
  }

  /**
   * Mount and unmount the live refresher here rather than in oninit: the
   * websocket lookup needs every bundle loaded, which is true by mount.
   */
  oncreate(vnode) {
    super.oncreate(vnode);
    watchForNewUpdates();
    setFeaturedRefresher(() => this.load(true));
  }
  onremove(vnode) {
    setFeaturedRefresher(null);
    super.onremove(vnode);
  }
  load(force) {
    if (force === void 0) {
      force = false;
    }
    // Reuse the last result across remounts — changing the sort order remounts
    // IndexPage, and the strip does not depend on the sort. A live update
    // clears the cache first, so `force` only guards against a race.
    const cached = force ? null : cachedFeatured();
    if (cached) {
      [this.lead, ...this.featured] = cached;
      this.loading = false;
      return;
    }

    // The big card belongs to the BlizzNux Launcher and nothing else: it is
    // always the newest launcher release, however many game patches and
    // driver releases have come out since (Karl, 2026-10-04). Every other
    // Update fills the smaller cards, newest first, and a launcher release
    // never appears among them — older releases still have their threads
    // under BlizzNux Launcher and Updates.
    // By tag ID, not slug: renaming a tag in admin regenerates its slug, and
    // that silently emptied the cards once (2026-10-04, battlenet →
    // battle-net-launcher). The IDs are the same ones the Updates bot posts with.
    const hasTag = (d, ids) => (d.tags?.() || []).some(t => t && ids.includes(String(t.id())));
    const isLauncher = d => hasTag(d, ['23']);
    const isUpdate = d => hasTag(d, ['11']);
    // Upstream releases — Proton-GE, Wine, DXVK, vkd3d-proton, ProtonDB
    // ratings — are posted for the record but never get a card (Karl,
    // 2026-10-04). They are still in the list below and under their tag.
    const isUpstream = d => hasTag(d, ['24']);
    // The small cards are GAMES only: a Blizzard game or Battle.net build
    // (Karl, 2026-10-04). The launcher pins its own Proton, so Proton, NVIDIA,
    // Mesa and the rest stay in the discussion list below without a card. A
    // game patch is the Updates thread that also carries a game tag; driver
    // and runtime releases carry Updates alone (or Upstream releases).
    // World of Warcraft, Diablo, Overwatch 2, Hearthstone, Battle.net Launcher,
    // Heroes of the Storm, StarCraft, Warcraft.
    const GAME_TAGS = ['2', '4', '6', '7', '9', '14', '15', '16'];
    const isGame = d => hasTag(d, GAME_TAGS);

    // The NEWEST release by version number, not by time. The bot posts every
    // release it has not delivered yet in one run, so several can share a
    // second — v0.2.23 to v0.2.26 did on 2026-10-04 — and neither createdAt
    // nor the id put them in version order. A thread whose title has no
    // version sorts below any that does, then falls back to time.
    const version = d => {
      const v = /v(\d+)\.(\d+)\.(\d+)/.exec(d.title?.() || '');
      return v ? [+v[1], +v[2], +v[3]] : [-1, -1, -1];
    };
    const newer = (a, b) => {
      const va = version(a),
        vb = version(b);
      for (let i = 0; i < 3; i++) if (va[i] !== vb[i]) return va[i] > vb[i];
      return (a.createdAt?.()?.getTime?.() || 0) > (b.createdAt?.()?.getTime?.() || 0);
    };
    const newest = list => list.reduce((best, d) => !best || newer(d, best) ? d : best, null);
    app.store
    // Pulls a full page: what is not shown yet backs "Show more" on a phone
    // and the Updates tag in the sidebar covers the rest.
    .find('discussions', {
      filter: {
        tag: 'updates'
      },
      sort: '-createdAt',
      page: {
        limit: 50
      }
    }).then(updates => {
      const all = Array.isArray(updates) ? updates : [];
      const lead = newest(all.filter(isLauncher));
      // Keep the WHOLE fetched page: "Show more" reveals from this rather
      // than issuing another request, so it is instant and cannot reshuffle
      // under the reader.
      const rest = all.filter(d => isGame(d) && !isLauncher(d) && !isUpstream(d));
      if (lead) return [lead, ...rest];

      // A busy week of patches can push the newest release off this page.
      // Look it up directly — but only a release thread (Updates AND
      // BlizzNux Launcher), never a member's question in the launcher tag.
      return app.store.find('discussions', {
        filter: {
          tag: 'launcher'
        },
        sort: '-createdAt',
        page: {
          limit: 20
        }
      }).then(recent => [newest((Array.isArray(recent) ? recent : []).filter(isUpdate)), ...rest]).catch(() => [null, ...rest]);
    }).then(items => {
      [this.lead, ...this.featured] = items;
      cacheFeatured(items);
      this.loading = false;
      // A forced reload keeps whatever the reader had already revealed.
      if (!force) this.shown = IndexHeader.initialShown();
      m.redraw();
    }).catch(() => {
      this.loading = false;
      m.redraw();
    });
  }
  featureCard(d, big) {
    const cover = coverFor(d);
    return m(Link, {
      key: d.id(),
      className: 'BnFeat' + (big ? ' BnFeat--big' : ''),
      href: '/d/' + d.slug()
    }, m("span", {
      className: 'BnFeat-cover' + (cover.isLogo ? ' BnFeat-cover--logo' : '')
    }, cover.url ? m("img", Object.assign({}, cover.isLogo ? {
      src: cover.url
    } : cdnImage(cover.url, big ? '(max-width: 767px) 100vw, 560px' : '(max-width: 767px) 100vw, 280px'), {
      alt: ""
    }, cover.isLogo ? {} : {
      width: '1200',
      height: '630',
      decoding: 'async'
    }, cover.isLogo || big ? {} : {
      loading: 'lazy'
    }, big && !cover.isLogo ? {
      fetchpriority: 'high'
    } : {})) : null), m("span", {
      className: "BnFeat-overlay"
    }), m("span", {
      className: "BnFeat-content"
    }, big ? m("span", {
      className: "BnFeat-tag BnFeat-tag--launcher"
    }, "Latest launcher release") : null, m("span", {
      className: "BnFeat-title"
    }, d.title()), m("span", {
      className: "BnFeat-meta"
    }, humanTime(d.createdAt()))));
  }

  /**
   * The strip in the shape it will have, before it has anything to put there.
   *
   * Without this the block renders nothing until the fetch lands and then
   * appears at full height, shoving the toolbar and the whole discussion list
   * down the page as the reader is looking at it. The placeholder reserves
   * exactly the layout the loaded strip will occupy — same card count for the
   * screen, same min-heights, same gaps — so nothing moves when the real cards
   * arrive.
   */
  skeleton() {
    const n = this.shown; // 6 on a desktop row, 3 stacked on a phone
    const side = Math.min(2, Math.max(0, n - 1));
    const more = Math.max(0, n - 3);
    const card = function (i, big) {
      if (big === void 0) {
        big = false;
      }
      return m('span', {
        key: i,
        className: 'BnFeat BnFeat--skeleton' + (big ? ' BnFeat--big' : '')
      });
    };
    return m("div", {
      className: "BnIndex",
      "aria-busy": "true"
    }, m("div", {
      className: "BnIndex-bar"
    }, m("h2", {
      className: "BnIndex-barTitle"
    }, "Latest Updates")), m("section", {
      className: "BnIndex-featured"
    }, m("div", {
      className: "BnIndex-lead"
    }, card(0, true)), side > 0 && m("div", {
      className: "BnIndex-side"
    }, Array.from({
      length: side
    }, (_, i) => card(i + 1)))), more > 0 && m("section", {
      className: "BnIndex-more"
    }, Array.from({
      length: more
    }, (_, i) => card(i + 3))), IndexHeader.isPhone() && m("div", {
      className: "BnIndex-moreBar BnIndex-moreBar--ghost"
    }));
  }
  view() {
    // A warm cache skips this entirely — `loading` is already false by the
    // time the first draw happens, so a re-mount does not flash a skeleton.
    if (this.loading) return this.skeleton();
    const lead = this.lead;
    const visible = this.featured.slice(0, this.shown - (lead ? 1 : 0));
    const side = lead ? visible.slice(0, 2) : []; // the two stacked beside the lead
    const more = lead ? visible.slice(2) : visible; // no release yet: the Updates sit in rows
    // Phones only, one click, one batch. Desktop already shows all six, and
    // past six the strip would compete with the discussions below it — so the
    // button appears only while a phone is still holding three back. Updates
    // are in the discussion list underneath now, and the Updates tag is in the
    // sidebar, so the strip does not need to link to its own archive. It says
    // "Show more", not "Load more": the whole page was fetched up front, so
    // nothing loads.
    const canExpand = IndexHeader.isPhone() && this.shown === 3 && this.featured.length + (lead ? 1 : 0) > 3;

    // Rendered inside IndexPage's content column, which is already inside the
    // page container — no container of its own.
    return m("div", {
      className: "BnIndex"
    }, m("div", {
      className: "BnIndex-bar"
    }, m("h2", {
      className: "BnIndex-barTitle"
    }, "Latest Updates")), lead &&
    // With nothing to put beside it the lead spans the full width, rather
    // than leaving a third of the row empty.
    m("section", {
      className: 'BnIndex-featured' + (side.length ? '' : ' BnIndex-featured--solo')
    }, m("div", {
      className: "BnIndex-lead"
    }, this.featureCard(lead, true)), side.length > 0 && m("div", {
      className: "BnIndex-side"
    }, side.map(d => this.featureCard(d, false)))), more.length > 0 && m("section", {
      className: "BnIndex-more"
    }, more.map(d => this.featureCard(d, false))), canExpand && m("div", {
      className: "BnIndex-moreBar"
    }, m(Button, {
      className: "Button BnIndex-showMore",
      onclick: () => {
        // Everything is already in memory — this only reveals more of
        // it, so there is no request and nothing to wait for. Going to
        // 6 also removes the button, by way of `canExpand`.
        this.shown = 6;
      }
    }, "Show more")));
  }
}
flarum.reg.add('blizznux-home', 'forum/components/IndexHeader', IndexHeader);